import { Trash2, GitMerge, Undo2 } from "lucide-react";

const ActionsRenderer = (props) => {
  const { data, context } = props;
  const setRowData = context?.setRowData;
  const refreshRowById = context?.refreshRowById;
  const status = data?.__status; // undefined | 'new' | 'deleted' | 'merged'
  const id = data?.__id ?? data?.id;
  const hasStatus = !!status;

  if (!setRowData || !id) return null;

  const updateAndRefresh = (updater) => {
    setRowData((prev) => {
      const next = updater(prev);
      // defer refresh so Grid sees the updated data
      setTimeout(() => refreshRowById && refreshRowById(id), 0);
      return next;
    });
  };

  const iconBtn =
    "inline-flex items-center justify-center h-6 w-6 rounded-md border text-white";

  // Determine visibility per action with optional consumer override
  const resolveVisibility = (actionName, defaultVisible) => {
    const av = context?.actionsVisibility;
    if (typeof av === "function") {
      const res = av(actionName, data);
      if (typeof res === "boolean") return res && defaultVisible;
    } else if (av && typeof av === "object") {
      const ent = av[actionName];
      if (typeof ent === "function") {
        const res = ent(data);
        if (typeof res === "boolean") return res && defaultVisible;
      } else if (typeof ent === "boolean") {
        return ent && defaultVisible;
      }
    }
    return defaultVisible;
  };

  const showDelete = resolveVisibility("delete", !hasStatus);
  const showMerge = resolveVisibility("merge", !hasStatus);
  const showUndo = resolveVisibility("undo", hasStatus);

  // Resolve disabled state per action via optional consumer overrides
  const resolveDisabled = (actionName) => {
    const ad = context?.actionsDisabled;
    if (typeof ad === "function") {
      const res = ad(actionName, data);
      if (typeof res === "boolean") return res;
    } else if (ad && typeof ad === "object") {
      const ent = ad[actionName];
      if (typeof ent === "function") {
        const res = ent(data);
        if (typeof res === "boolean") return res;
      } else if (typeof ent === "boolean") {
        return ent;
      }
    }
    return false;
  };

  return (
    <div className="h-full flex items-center justify-center gap-1 w-full">
      {showDelete && (
        <>
          <button
            title="Delete"
            aria-label="Delete"
            className={`${iconBtn} bg-red-600 border-red-600 hover:bg-red-700`}
            onClick={() => {
              updateAndRefresh((prev) =>
                prev.map((r) =>
                  (r.__id ?? r.id) === id ? { ...r, __status: "deleted" } : r
                )
              );
            }}
          >
            <Trash2 size={16} />
          </button>
          {showMerge && (
            <button
              title="Merge"
              aria-label="Merge"
              disabled={resolveDisabled("merge")}
              className={`${iconBtn} bg-amber-500 border-amber-500 hover:bg-amber-600 ${
                resolveDisabled("merge")
                  ? "opacity-50 cursor-not-allowed hover:bg-amber-500"
                  : ""
              }`}
              onClick={() => {
                if (resolveDisabled("merge")) return;
                updateAndRefresh((prev) =>
                  prev.map((r) =>
                    (r.__id ?? r.id) === id ? { ...r, __status: "merged" } : r
                  )
                );
                // Notify consumer generically that a row was merged
                if (typeof context?.onRowMerged === "function") {
                  try {
                    context.onRowMerged(id, data);
                  } catch (_) {}
                }
              }}
            >
              <GitMerge size={16} />
            </button>
          )}
        </>
      )}
      {showUndo && (
        <button
          title="Undo"
          aria-label="Undo"
          className="inline-flex items-center justify-center h-6 w-6 rounded-md border border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
          onClick={() => {
            // Allow consumer to handle undo for specific statuses
            if (
              status === "merged" &&
              typeof context?.onRowUndo === "function"
            ) {
              try {
                context.onRowUndo(id, data, status);
                return;
              } catch (_) {}
            }
            updateAndRefresh((prev) =>
              status === "new"
                ? prev.filter((r) => (r.__id ?? r.id) !== id)
                : prev.map((r) =>
                    (r.__id ?? r.id) === id ? { ...r, __status: undefined } : r
                  )
            );
          }}
        >
          <Undo2 size={16} />
        </button>
      )}
    </div>
  );
};

export default ActionsRenderer;
